import React from 'react'
import styled from '@emotion/styled'
import { AccountHeader } from '@mobi/component-library/Account'
import { Button } from '@mobi/component-library/Common/V2'
import { colors, font } from '@mobi/component-library/Theme/Common'
import { Content, Footer, SignUpPageStyled } from './SignUpPage.styled'
import { OnPasswordInputChangeProps, PasswordInput } from '@mobi/component-library/Account'
import { useDispatch, useSelector } from 'react-redux'
import {
  getSignUpState,
  setAccountSignUp,
  setInternetProspect,
  setRwwaRanking,
  setVerification,
} from '../Store'
import { useMutation } from '@tanstack/react-query'
import { trackSignUpCreatePassword } from '../../Verification/Utils/ga'
import { trackSignUpError } from '../Utils/ga'
import { useHistory } from '@mobi/account/Hooks'
import { unwrapErrorMessage } from '@mobi/utils/errors'
import { post, queryKeys, SignUpUrl } from '@mobi/account/Utils'
import { LogonRequestData, LogonResult } from '@mobi/api-types'
import SignupError from './SignupError'

type PropPassword = {
  onCompleted: ([betAccountNumber, userFirstName]: [number, string]) => void
  attemptLogin: (data: LogonRequestData) => Promise<
    LogonResult & {
      errorMessage?: string
    }
  >
}

type TypeCreateBetAccountResponse = {
  betAccountNumber: number
}

type TypeCreateBetAccountRequest = {
  applicationReferenceNumber: number
  password: string
  enquiryId: string
}

export const Password = ({ onCompleted, attemptLogin }: PropPassword) => {
  const [password, setPassword] = React.useState('')
  const [canContinue, setCanContinue] = React.useState(false)
  const signUpState = useSelector(getSignUpState)
  const dispatch = useDispatch()
  const history = useHistory()

  const mutation = useMutation<TypeCreateBetAccountResponse, Error, TypeCreateBetAccountRequest>({
    mutationFn: ({
      applicationReferenceNumber,
      password,
      enquiryId,
    }: TypeCreateBetAccountRequest) => {
      return post(SignUpUrl.createBetAccount, {
        applicationReferenceNumber,
        password,
        enquiryId,
      })
    },
    mutationKey: [queryKeys.signupCreateWageringBetAccount],
    onSuccess: (data, { password }) => {
      attemptLogin({
        accountIdentifier: data.betAccountNumber.toString(),
        password,
        keepLoggedIn: false,
        rememberAccount: false,
        deviceSecret: 'unspecified',
      })
      onCompleted([data.betAccountNumber, signUpState?.signUp?.FirstName || ''])
      dispatch(setRwwaRanking(parseInt(signUpState?.verification?.RwwaRanking || '0')))
      dispatch(setAccountSignUp(undefined))
      dispatch(setVerification(undefined))
      dispatch(setInternetProspect(undefined))
      trackSignUpCreatePassword(
        signUpState?.verification?.AddressVerified ? 'verified-address' : 'verified-identification'
      )
    },
    onError: error => {
      trackSignUpError(history.location.pathname, unwrapErrorMessage(error))
    },
  })

  const handlePasswordInputChange = ({ password, canContinue }: OnPasswordInputChangeProps) => {
    setPassword(password)
    setCanContinue(canContinue)
  }

  if (
    !signUpState ||
    !signUpState.signUp ||
    !signUpState.verification ||
    !signUpState.internetProspect ||
    !signUpState.internetProspect.ApplicationReferenceNumber ||
    signUpState.verification.RwwaRanking !== '100'
  ) {
    return null
  }

  const savePassword = () => {
    if (signUpState.internetProspect && signUpState.verification) {
      mutation.mutate({
        applicationReferenceNumber: signUpState.internetProspect.ApplicationReferenceNumber,
        password: password,
        enquiryId: signUpState.verification?.EnquiryId,
      })
    }
  }

  if (mutation.isError) {
    return (
      <SignupError
        isOpen={true}
        onClose={() => mutation.reset()}
        applicationId={signUpState.internetProspect.ApplicationReferenceNumber?.toString()}
      />
    )
  }

  return (
    <SignUpPageStyled>
      <AccountHeader
        alignItems='start'
        title='Create your password'
        description='Create a password to keep your account secured.'
        style={{ marginBottom: '0rem' }}
      />
      <Content>
        <PasswordInput
          onPasswordInputChange={handlePasswordInputChange}
          isDisabled={mutation.isPending}
          trackSignUpError={trackSignUpError}
        />
      </Content>
      <Footer>
        <Button
          aria-label='Continue button'
          color='primary'
          isFullWidth
          size='md'
          disabled={!canContinue}
          isLoading={mutation.isPending}
          onClick={savePassword}
        >
          Continue
        </Button>
        <HelperText>Complete all fields before continuing</HelperText>
      </Footer>
    </SignUpPageStyled>
  )
}

export const HelperText = styled.div({
  fontFamily: font.family.primary,
  color: colors.neutral[600],
  ...font.size.md,
})
